import React, { useEffect, useState } from 'react';
import { Box, Container, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,  } from '@mui/material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { toast } from 'react-toastify';
import ClassificationBannerHeader from '../../common/banner/classifactionBannerHeader.js';
import PageHeader from '../../common/banner/pageHeader.js';
import { sortAdminItems } from '../../common/utils/tableUtil.js';
import SearchBar from '../../common/search/SearchBar.js';
import AdminAppModal from './modals/adminAppModal.js';
import AdminAppDeleteModal from './modals/adminAppDeleteModal.js';
import AdminAppDeleteSimDataModal from './modals/adminAppDeleteSimDataModal.js';

const AdminApplicationsTable = ({ items, onToggle, sortConfig, onSortChange, fetchApplications, setApplications }) => {

  
  const handleUpdate = (application) =>  {    
    toast.success(`Application With IP ${application.ip_dns} Updated`, { autoClose: 3000 });
    fetchApplications();
  }

  const handleDelete = (application) =>  {
    setApplications(items.filter(items => items.id !== application.id));
    toast.success(`Application With IP ${application.ip_dns} Deleted`, { autoClose: 3000 });
  }
  
  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key, direction });
  }

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowDropUp fontSize="small" />
    ) : (
      <ArrowDropDown fontSize="small" />
    );
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">    
          <TableHead>
            <TableRow sx={{ backgroundColor: '#0568AE' }}>
              {['IP', 'Collector', 'App Name', 'Port', 'Authorized', 'Notes'].map((field) => (
                <TableCell
                  key={field}
                  onClick={() => handleSort(field)}
                  sx={{ color: '#EEEEEE', fontWeight: 'bold', cursor: 'pointer', attackSelect: 'none' }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                    <SortIcon field={field} />
                  </Stack>
                </TableCell>                
              ))}
              <TableCell sx={{ color: '#EEEEEE', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={item.id} sx={{ height: 56, backgroundColor: index % 2 === 0 ? '#2A2A2A' : '#000000' }}>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.ip_dns}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.collector}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.name}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.port}</TableCell>
                <TableCell> <Switch checked={!!item.authorized} onChange={(e) => onToggle(item, 'active', e.target.checked)} /> </TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.notes}</TableCell>
                <TableCell>
                    <Stack direction="row" spacing={1}>
                        <AdminAppModal application={ item } onTriggerUpdate={handleUpdate} />
                        <AdminAppDeleteModal application={ item } onTriggerDelete={handleDelete} /> 
                    </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default function AdminApplications() {
    const [applications, setApplications] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const fetchApplications = async () => {
        try {
                
            const response = await fetch(`${process.env.REACT_APP_API}/applications/`);
            if (!response.ok) {
                toast.error(`HTTP error! status: ${response.status}`);
            } else {
                const json = await response.json();
                setApplications(json.data);
            }
        } catch (error) {
            toast.error(error);
        } 
    };

    const handleCreate = (application) =>  {
      toast.success(`Application With IP ${application.ip_dns} Created`, { autoClose: 3000 });
      fetchApplications();
    }

    const handleDelete = (recordsDeleted) =>  {
      if (recordsDeleted) {
        toast.success(`Simulator Created Records Deleted`, { autoClose: 3000 });
        fetchApplications();
      }
    }

    const handleUpdate = async (application) => {
         const res = await fetch(`${process.env.REACT_APP_API}/applications/update`, {
             method: 'PUT',
             headers: { 'Content-Type': 'application/json' },
             body: JSON.stringify(application)
         });
         if (!res.ok) {
            toast.error(`Failed to update application : ${res.status}`);
          } else {
            setApplications((prev) => prev.map((p) => (p.id === application.id ? application : p)));
          }
     };

    const handleSortChange = (newConfig) => {
        setSortConfig(newConfig);
    };

    const filtered = applications?.filter((item) => {
        const s = searchQuery.toLowerCase();
        return ['ip_dns', 'collector', 'name', 'port', 'authorized', 'notes'].some((field) => item[field]?.toLowerCase().includes(s));
    });

  const sorted = sortAdminItems(filtered, sortConfig);
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
      fetchApplications();
  }, []);

  return (
    <Container sx={{ minWidth:1800, mt: 10, mb: 10 }}>
        <Box sx={{pt: 2}}>
            <ClassificationBannerHeader />
            <PageHeader title="Admin Applications" />
            <Stack direction="row" justifyContent="flex-end" mt={2} mb={1} sx={{ width: '100%', mx: 'auto' }}>                    
                  <SearchBar
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search Applications..."
                      label="Search Applications..."
                      width={200}
                  />                
            </Stack>
          
            <TableContainer component={Paper} sx={{ mt: 4, backgroundColor: '#1A1A1A'  }}>
                <AdminApplicationsTable
                    items={paginated}
                    onToggle={(item, key, value) => handleUpdate({ ...item, [key]: value })}
                    sortConfig={sortConfig}
                    onSortChange={handleSortChange}
                    fetchApplications={fetchApplications}
                    setApplications={setApplications}
                />
                <TablePagination
                    component="div"
                    count={sorted.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
                />
            </TableContainer>
            <AdminAppModal application='' onTriggerCreate={handleCreate} />
            <AdminAppDeleteSimDataModal onTriggerDelete={handleDelete} />
        </Box>
    </Container>
  )
}
